import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

const API = "/api";

const AuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { handleCallback, setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Check for Emergent Google Auth session_id in hash fragment
      const hash = window.location.hash;
      if (hash && hash.includes('session_id=')) {
        const sessionId = hash.split('session_id=')[1]?.split('&')[0];
        if (sessionId) {
          try {
            const response = await axios.post(`${API}/auth/google/session`, {
              session_id: sessionId
            });
            // Use existing JWT flow - save token and user
            setUser(response.data.user, response.data.token);
            toast.success(`Welcome, ${response.data.user.name}!`);
            // Clear the hash and navigate home
            window.location.hash = '';
            navigate('/', { replace: true });
            return;
          } catch (error) {
            console.error('Google auth failed:', error);
            toast.error('Google sign-in failed. Please try again.');
            navigate('/login', { replace: true });
            return;
          }
        }
      }

      // Legacy: Check for token in query params (old flow)
      const token = searchParams.get('token');
      if (token) {
        handleCallback(token);
        navigate('/', { replace: true });
        return;
      }

      // No auth data found
      navigate('/', { replace: true });
    };

    processAuth();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="auth-callback">
      <div className="text-center space-y-4">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
